import { ChangeEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  DataGridPremium as DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
} from '@mui/x-data-grid-premium';
import { format } from 'date-fns';
import { upperCaseFirst } from 'helpers';
import {
  EstateTaskInstance,
  EstateTaskInstanceStatus,
  useEstate_GetEstateByIdQuery,
  useEstateTaskInstance_GetAllEstateTaskInstancesByEstateIdQuery,
  useEstateTaskInstance_UpdateEstateTaskInstanceMutation,
} from 'queries';
import { MoreVertical } from 'ts-react-feather-icons';

import Spinner from 'common/spinner';
import HomeLayout from 'layouts/home-layout';
import { EstateTaskGroupLabel } from 'utils/estate';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  root: {
    // no border around the cells when focused
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
  },
  container: {
    padding: '5%',
    flexGrow: 1,
  },
  listAction: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  nameWrapper: {
    display: 'block',
    lineHeight: '1.43',
  },
  statusAssigned: {
    color: 'blue',
  },
  statusCompleted: {
    color: 'green',
  },
  tabs: { minHeight: '15px' },
  tab: { minHeight: '15px', minWidth: '60px' },
}));

export default function SuperAdminEstateTasks() {
  const classes = useStyles();
  const { estateId } = useParams<Record<string, string>>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tabSelected, setTabSelected] = useState<number>(0);

  const { data: estateData, loading: estateLoading } = useEstate_GetEstateByIdQuery({
    variables: {
      id: estateId,
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting estate by ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const getEstateTaskInstanceStatusesFilter = () => {
    let status;
    switch (tabSelected) {
      case 0:
        break;
      case 1:
        status = EstateTaskInstanceStatus.Unassigned;
        break;
      case 2:
        status = EstateTaskInstanceStatus.Assigned;
        break;
      case 3:
        status = EstateTaskInstanceStatus.Completed;
        break;
      default:
        break;
    }
    return status ? [status] : undefined;
  };

  const {
    data: tasksData,
    loading: tasksLoading,
    refetch: refetchEstateTaskInstances,
  } = useEstateTaskInstance_GetAllEstateTaskInstancesByEstateIdQuery({
    skip: !estateData?.data?.portalId,
    variables: {
      input: {
        estateId: estateId,
        portalId: estateData?.data?.portalId || '',
        filter: {
          statuses: getEstateTaskInstanceStatusesFilter(),
          isArchived: false,
        },
      },
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting all estate task instances by estate ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [updateTaskInstance] = useEstateTaskInstance_UpdateEstateTaskInstanceMutation({
    onError: (e) => {
      showAlert({
        message: 'Error updating estate task instance: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleActionClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleActionMenuClick = () => {
    setAnchorEl(null);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateTaskInstanceStatus = (item: any, status: EstateTaskInstanceStatus) => {
    let actionVerb = '';
    if (status === EstateTaskInstanceStatus.Unassigned) {
      actionVerb = 'unassigned';
    } else if (status === EstateTaskInstanceStatus.Assigned) {
      actionVerb = 'assigned';
    } else if (status === EstateTaskInstanceStatus.Completed) {
      actionVerb = 'completed';
    }

    updateTaskInstance({
      variables: {
        input: {
          id: item.id,
          status: status,
        },
      },
      onCompleted: () => {
        handleActionMenuClick();
        showToast({
          message: `Task marked as ${actionVerb}.`,
          severity: NotificationSeverity.SUCCESS,
        });
      },
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleMarkUnassignedClick = (item: any) => {
    updateTaskInstanceStatus(item, EstateTaskInstanceStatus.Unassigned);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleMarkAssignedClick = (item: any) => {
    updateTaskInstanceStatus(item, EstateTaskInstanceStatus.Assigned);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleMarkCompletedClick = (item: any) => {
    updateTaskInstanceStatus(item, EstateTaskInstanceStatus.Completed);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleTabChange = (e: ChangeEvent<any>, newValue: number) => {
    setTabSelected(newValue);
  };

  useEffect(() => {
    if (estateData) {
      refetchEstateTaskInstances({
        input: {
          estateId: estateId,
          portalId: estateData.data.portalId,
          filter: {
            statuses: getEstateTaskInstanceStatusesFilter(),
            isArchived: false,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabSelected, estateData]);

  if (estateLoading || !estateData || tasksLoading || !tasksData) {
    return <Spinner />;
  }

  const columns: GridColDef[] = [
    {
      disableColumnMenu: true,
      field: 'group',
      filterable: false,
      flex: 2,
      headerName: 'Group',
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as EstateTaskInstance;
        const groupName = EstateTaskGroupLabel[row.group!];
        return (
          <Tooltip title={groupName}>
            <div>{groupName}</div>
          </Tooltip>
        );
      },
      valueGetter: (params: GridValueGetterParams) => params.row.group,
    },
    {
      disableColumnMenu: true,
      field: 'label',
      filterable: false,
      flex: 6,
      headerName: 'Label',
      renderCell: (params: GridRenderCellParams) => {
        const label = params.row.labelContext
          ? `${params.row.label} (${params.row.labelContext})`
          : params.row.label;

        return (
          <Tooltip title={label}>
            <div>{label}</div>
          </Tooltip>
        );
      },
      valueGetter: (params: GridValueGetterParams) => {
        const label = params.row.labelContext
          ? `${params.row.label} (${params.row.labelContext})`
          : params.row.label;

        return label;
      },
    },
    {
      disableColumnMenu: true,
      field: 'updated',
      filterable: false,
      flex: 2,
      headerName: 'Updated',
      renderCell: (params: GridRenderCellParams) => {
        const updated = params.row.updatedAt;
        const date: string = format(Date.parse(updated!.toString()), 'LLL d y');
        const time: string = format(Date.parse(updated!.toString()), 'h:mm bbb');
        return (
          <div className={classes.nameWrapper}>
            <div>
              <Typography variant="caption">{date}</Typography>
            </div>
            <div className={classes.nameWrapper}>
              <Typography variant="caption">{time}</Typography>
            </div>
          </div>
        );
      },
      valueGetter: (params: GridValueGetterParams) => params.row.updatedAt,
    },
    {
      disableColumnMenu: true,
      field: 'status',
      filterable: false,
      flex: 2,
      headerName: 'Task Status',
      renderCell: (params: GridRenderCellParams) => {
        let statusLabel = <>{upperCaseFirst(params.row.status)}</>;
        if (params.row.status === EstateTaskInstanceStatus.Completed) {
          statusLabel = (
            <Chip
              className={classes.statusCompleted}
              size="small"
              label={upperCaseFirst(params.row.status)}
              variant="outlined"
            />
          );
        } else if (params.row.status === EstateTaskInstanceStatus.Assigned) {
          statusLabel = (
            <Chip
              className={classes.statusAssigned}
              size="small"
              label={upperCaseFirst(params.row.status)}
              variant="outlined"
            />
          );
        }
        return (
          <>
            {statusLabel}
            <List style={{ position: 'absolute', right: 0 }}>
              <ListItem>
                <ListItemSecondaryAction className={classes.listAction}>
                  <IconButton
                    id={params.row.id}
                    edge="end"
                    onClick={(e) => {
                      handleActionClick(e);
                    }}
                    aria-controls={params.row.id}
                    size="large"
                  >
                    <MoreVertical />
                  </IconButton>
                  <Menu
                    open={anchorEl?.id === params.row.id}
                    anchorEl={anchorEl}
                    keepMounted
                    onClose={() => handleActionMenuClick()}
                  >
                    <MenuItem
                      key="mark unassigned"
                      onClick={() => {
                        handleMarkUnassignedClick(params.row);
                      }}
                      disabled={params.row.status === EstateTaskInstanceStatus.Unassigned}
                    >
                      Task status: mark unassigned
                    </MenuItem>
                    <MenuItem
                      key="mark assigned"
                      onClick={() => {
                        handleMarkAssignedClick(params.row);
                      }}
                      disabled={params.row.status === EstateTaskInstanceStatus.Assigned}
                    >
                      Task status: mark assigned
                    </MenuItem>
                    <MenuItem
                      key="mark completed"
                      onClick={() => {
                        handleMarkCompletedClick(params.row);
                      }}
                      disabled={params.row.status === EstateTaskInstanceStatus.Completed}
                    >
                      Task status: mark completed
                    </MenuItem>
                  </Menu>
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </>
        );
      },
      valueGetter: (params: GridValueGetterParams) => params.row.status,
    },
  ];

  const rows = [];
  const tasks = tasksData ? tasksData.data : [];
  for (const task of tasks) {
    rows.push(task);
  }

  return (
    <HomeLayout showPortalName={false}>
      <div className={classes.container}>
        <Tabs
          value={tabSelected}
          indicatorColor="primary"
          textColor="primary"
          onChange={handleTabChange}
          className={classes.tabs}
        >
          <Tab className={classes.tab} label="All" />
          <Tab className={classes.tab} label="Unassigned" />
          <Tab className={classes.tab} label="Assigned" />
          <Tab className={classes.tab} label="Completed" />
        </Tabs>

        <DataGrid
          className={classes.root}
          columns={columns}
          rows={rows}
          disableColumnReorder
          disableRowSelectionOnClick
          autoHeight={true}
          rowHeight={80}
          pagination
          initialState={{
            pagination: { paginationModel: { pageSize: 100 } },
          }}
        />
      </div>
    </HomeLayout>
  );
}
